import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import useFetch from "../../hooks/useFetch";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faCheck,faTimes, } from "@fortawesome/free-solid-svg-icons";

import { THEMES } from "./themes/pdThemes";

const languages = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिंदी" },
  { code: "bn", label: "বাংলা" },
  { code: "mr", label: "मराठी" },
  { code: "gu", label: "ગુજરાતી" },
  { code: "ta", label: "தமிழ்" },
  { code: "te", label: "తెలుగు" },
  { code: "kn", label: "ಕನ್ನಡ" },
  { code: "ml", label: "മലയാളം" },
  { code: "pa", label: "ਪੰਜਾਬੀ" },
  { code: "as", label: "অসমীয়া" },
  { code: "ur", label: "اردو" },
  { code: "ar", label: "العربية" },
  { code: "ja", label: "日本語" },
  { code: "zh", label: "中文" },
  { code: "ko", label: "한국어" },
  { code: "es", label: "Español" },
  { code: "pt", label: "Português" },
  { code: "fr", label: "Français" },
  { code: "id", label: "Bahasa Indonesia" },
  { code: "ms", label: "Bahasa Melayu" },
  { code: "de", label: "Deutsch" },
  { code: "tr", label: "Türkçe" }
];

const countryOptions = [
    "India",
    "United States",
    "United Kingdom",
    "Canada",
    "Australia",
    "UAE",
    "Singapore",
    "Bangladesh",
    "Nepal",
    "Sri Lanka",

    "Argentina",
    "Austria",
    "Bahrain",
    "Belgium",
    "Bhutan",
    "Bolivia",
    "Brazil",
    "Chile",
    "China",
    "Colombia",
    "Czech Republic",
    "Denmark",
    "Egypt",
    "Finland",
    "France",
    "Germany",
    "Greece",
    "Hong Kong",
    "Hungary",
    "Indonesia",
    "Ireland",
    "Israel",
    "Italy",
    "Japan",
    "Kenya",
    "Kuwait",
    "Malaysia",
    "Maldives",
    "Mexico",
    "Netherlands",
    "New Zealand",
    "Nigeria",
    "Norway",
    "Oman",
    "Pakistan",
    "Paraguay",
    "Peru",
    "Philippines",
    "Poland",
    "Portugal",
    "Qatar",
    "Russia",
    "Saudi Arabia",
    "South Africa",
    "South Korea",
    "Spain",
    "Sweden",
    "Switzerland",
    "Taiwan",
    "Thailand",
    "Turkey",
    "Ukraine",
    "Uruguay",
    "Venezuela",
    "Vietnam"
];

const genderOptions = [
  "Male",
  "Female",
  "Neutral",
  "Unknown"
];

const SnapId = () => {
    const authState = useSelector((state) => state.authReducer);
    const dispatch = useDispatch();
    const [fetchData, { loading }] = useFetch();
    const [isUpdating, setIsUpdating] = useState(false);
    const [editField, setEditField] = useState(null);
    const [editLabel, setEditLabel] = useState("");
    const [editValue, setEditValue] = useState("");
    const [globalMessage, setGlobalMessage] = useState("");

    const [updateStatus, setUpdateStatus] = useState(""); // "", "success", "error"

    const profileFields = [
        { key: "mobile", label: "Mobile" },
        { key: "country", label: "Country" },
        { key: "city", label: "City / Place" },
        { key: "area", label: "Area / Locality / Neighbourhood" },
        { key: "pinCode", label: "PIN/Postal Code/PO Box/ZIP" },
        { key: "state", label: "State / Province" },
        { key: "remarks", label: "Profile Headline" },
        { key: "gender", label: "Gender" },
        { key: "language", label: "Language" },
        { key: "theme", label: "Theme" }
        // { key: "isActive", label: "Active" },
        // { key: "inactiveReason", label: "Inactive Reason" },
        // { key: "email", label: "Email" }
    ];

    const openEditor = (field, value) => {
        setUpdateStatus("");
        const fieldMeta = profileFields.find((f) => f.key === field);

        setEditField(field);
        setEditLabel(fieldMeta?.label || field);

        if (field === "gender") {
            setEditValue(value || "Unknown");
        } else if (field === "language") {
            setEditValue(value || "en");
        } else if (field === "theme") {
            setEditValue(value || "DARK");
        } else if (field === "country") {
            // More robust matching
            const normalizedValue = String(value || "").trim().toLowerCase();
            
            const matchedCountry = countryOptions.find(c => 
                c.toLowerCase().trim() === normalizedValue
            ) || normalizedValue;   // fallback to original if not in list

            setEditValue(matchedCountry || "");
        } else {
            setEditValue(value || "");
        }
    };

    const closeEditor = () => {
        setEditField(null);
        setEditValue("");
    };

    const filledFields = profileFields.filter(
        ({ key }) =>
            authState.user?.[key] &&
            String(authState.user[key]).trim() !== ""
    ).length;

    const totalFields = profileFields.length;

    let profileStatus = "";
    let profileStatusClass = "";

    if (filledFields === 0) {
        profileStatus = "⚠️ INCOMPLETE VITAL PROFILE";
        profileStatusClass =
            "text-[10px] bg-red-500/20 text-red-200 border border-red-400/50";
    } else if (filledFields < totalFields) {
        profileStatus = "🟡 PARTIALLY COMPLETE PROFILE";
        profileStatusClass =
            "text-[10px] bg-yellow-500/20 text-yellow-100 border border-yellow-400/50";
    } else {
        profileStatus = "✓ VERIFIED THINK5WHYS MEMBER";
        profileStatusClass =
            "text-[10px] bg-green-500/20 text-green-100 border border-green-400/50";
    }


    const ProfileFieldCard = ({ field, label, value, openEditor}) => {

        let displayValue = value;

        if (field === "language") {
            displayValue =
                languages.find(
                    (l) => l.code === value
                )?.label || value;
        }

        if (field === "theme") {
            displayValue =
                THEMES[value]?.name || value;
        }

        return (
            <div className="rounded-xl bg-white/5 p-3 relative">

                <button
                    className="text-xs w-6 absolute top-2 right-2 text-cyan-300 hover:text-cyan-100 transition"
                    onClick={() => {
                            if (
                                field === "country" &&
                                authState.user?.country &&
                                authState.user.country.trim() !== ""
                            ) {
                                return;
                            }

                            openEditor(field, value);
                        }}
                     >
                    <FontAwesomeIcon
                        icon={faPen}
                        size="xs"
                    />
                </button>

                <div className="text-[10px] uppercase text-gray-200 pr-5">
                    {label}
                </div>

                <div className="text-sm text-white mt-1">
                    {displayValue || (
                        <span className="text-yellow-300 italic">
                            Not Provided
                        </span>
                    )}
                </div>

            </div>
        );
    };

    const [timeSinceJoin, setTimeSinceJoin] = useState("");

    useEffect(() => {
        const joiningTime = new Date(authState?.user?.createdAt);

        const interval = setInterval(() => {
            const now = new Date();
            const diff = now - joiningTime;

            const seconds = Math.floor(diff / 1000) % 60;
            const minutes = Math.floor(diff / (1000 * 60)) % 60;
            const hours = Math.floor(diff / (1000 * 60 * 60)) % 24;
            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
            const years = Math.floor(days / 365);
            const months = Math.floor((days % 365) / 30);

            setTimeSinceJoin(
                `${years}Y : ${months}M : ${days}D : ${hours}H : ${minutes}M : ${seconds}S`
            );
        }, 1000);

        return () => clearInterval(interval);
    }, [authState?.user?.joiningTime]);

    const handleUpdateProfileField = async () => {
        if (!editField) return;
        setIsUpdating(true);
        setUpdateStatus("");

        const config = {
            url: "/auth/update-profile-field",
            method: "post",
            data: {
                userId: authState.user._id,
                field: editField,
                value: editValue
            },
            headers: {
                Authorization: authState.token
            }
        };

        fetchData(config) .then(response => {
                if (
                    response?.data?.success ||
                    response?.success ||
                    response?.status === 200
                ) {
                    dispatch({
                        type: "LOGIN",
                        payload: {
                            ...authState,
                            user: {
                                ...authState.user,
                                [editField]: editValue
                            }
                        }
                    });
                    authState.user[editField] = editValue;
                    setUpdateStatus("success");
                    setGlobalMessage(`✅ ${editLabel} updated successfully`);
                    
                    // closeEditor();
                    setTimeout(() => {
                        setGlobalMessage("");
                    }, 3000);
                }
            })
            .catch(error => {
                setUpdateStatus("error");
                console.error(
                    `Error updating ${editField}:`,
                    error
                );
            })
            .finally(() => {
                setIsUpdating(false);
            });
    };
    
    return (
        <div className="space-y-6 bg-gray-900 p-6 rounded-xl text-white border-t-2 border-cyan-200 mt-2">
            {globalMessage && (
                <div className="fixed top-4 right-4 z-[9999]
                    rounded-xl bg-emerald-600 px-4 py-2
                    text-sm text-white shadow-xl">
                    {globalMessage}
                </div>
            )}
            <div
                className="
                    relative overflow-hidden
                    rounded-3xl
                    border border-cyan-400/30
                    bg-gradient-to-br
                    from-slate-900
                    via-purple-950
                    to-cyan-950
                    p-4
                    shadow-2xl
                    backdrop-blur
                "
            >
               {/* <button onClick={() => setActiveTab("")}
                    className=" absolute top-3 right-3 h-8 w-8 rounded-full bg-white/10 text-white hover:bg-white/20"
                >
                    ✕
                </button> */}

                <div className="flex items-center gap-3 mb-4">
                    <div className=" h-8 w-8 rounded-xl bg-cyan-500/20 flex items-center justify-center text-xl">
                        🪪
                    </div>
                    <div>
                        <div className="text-sm lg:text-lg font-bold text-cyan-300">
                            Think5Whys IDSnap
                        </div>
                        <div className="text-[10px] lg:text-xs text-gray-200">
                            Instant & Vital Digital Identity on Think5Whys Platfrom
                        </div>
                    </div>
                </div>
                <div className="mb-4">
                    <div className="text-sm lg:text-lg font-bold text-white">
                        {authState.user?.name}
                    </div>
                    <div className="text-cyan-200 text-sm">
                        #{authState.user?.think5whyscode}
                    </div>
                </div>
                <div className="mb-2 bg-gradient-to-br from-slate-900 via-purple-950 to-cyan-950 rounded-md p-1">
                    {/* <ProfileFieldCard field="email" label="Email" value={authState.user?.email}
                        openEditor={openEditor}
                    /> */}
                    <div className="text-[10px] uppercase text-gray-200">
                        Email
                    </div>
                    <div className="text-sm text-white break-all">
                        {authState.user?.email}
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                    {profileFields.map(({ key, label }) => (
                        <ProfileFieldCard
                            key={key}
                            field={key}
                            label={label}
                            value={authState.user?.[key]}
                            openEditor={openEditor}
                        />
                    ))}
                </div>
            </div>
            {editField && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
                    <div className="w-[500px] rounded-2xl bg-slate-900 p-5 border border-cyan-500/30">
                        <div className="text-center text-white font-bold text-sm lg:text-lg mb-4">
                            Update {editLabel}
                        </div>
                        {updateStatus === "success" && (
                            <div className="mt-3 text-green-400 text-sm">
                                ✅ Updated successfully
                            </div>
                        )}

                        {updateStatus === "error" && (
                            <div className="mt-3 text-red-400 text-sm">
                                ❌ Update failed
                            </div>
                        )}
                        
                        {editField === "gender" ? (
                            <select
                                value={editValue}
                                onChange={(e) =>
                                    setEditValue(e.target.value)
                                }
                                className="w-full rounded-xl bg-white/10 border border-white/10 p-1 text-xs text-white outline-none"
                            >
                                {genderOptions.map((g) => (
                                    <option
                                        key={g}
                                        value={g}
                                        className="text-black"
                                    >
                                        {g}
                                    </option>
                                ))}
                            </select>
                        ) : editField === "language" ? (
                            <select
                                value={editValue}
                                onChange={(e) =>
                                    setEditValue(e.target.value)
                                }
                                className="w-full rounded-xl bg-white/10 border border-white/10 p-1 text-xs text-white outline-none"
                            >
                                {languages.map((lang) => (
                                    <option
                                        key={lang.code}
                                        value={lang.code}
                                        className="text-black"
                                    >
                                        {lang.label}
                                    </option>
                                ))}
                            </select>
                        ) : editField === "country" ? (
                            <select
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                className="w-full rounded-xl bg-white/10 border border-white/10 px-3 py-1 text-sm text-white outline-none"
                            >
                                <option value="" className="text-black">
                                    Select Country
                                </option>

                                {countryOptions.map((country) => (
                                    <option
                                        key={country}
                                        value={country}
                                        className="text-black"
                                    >
                                        {country}
                                    </option>
                                ))}
                            </select>
                        ) : editField === "theme" ? (
                            <select
                                value={editValue}
                                onChange={(e) =>
                                    setEditValue(e.target.value)
                                }
                                className="w-full rounded-xl bg-white/10 border border-white/10 p-1 text-xs text-white outline-none"
                            >
                                {Object.entries(THEMES).map(
                                    ([key, theme]) => (
                                        <option
                                            key={key}
                                            value={key}
                                            className="text-black"
                                        >
                                            {theme.name}
                                        </option>
                                    )
                                )}
                            </select>
                        ) : (
                            <input
                                type={
                                    editField === "mobile"
                                        ? "tel"
                                        : editField === "email"
                                        ? "email"
                                        : editField === "pinCode"
                                        ? "number"
                                        : "text"
                                }
                                value={editValue}
                                onChange={(e) =>
                                    setEditValue(e.target.value)
                                }
                                style={{ width: "100%" }}
                                className="rounded-xl bg-white/10 border border-white/10 p-3 text-white outline-none"
                                autoFocus
                            />
                        )}
                        <div className="flex gap-3 mt-5">
                            <button
                                onClick={closeEditor}
                                className="h-12 w-12 rounded-full bg-red-500/20 text-red-200 border border-red-500/30 flex items-center justify-center"
                            >
                                <FontAwesomeIcon
                                    icon={faTimes}
                                    className="h-6 w-6"
                                />
                            </button>
                            <button
                                disabled={isUpdating || updateStatus === "success"}
                                onClick={handleUpdateProfileField}
                                className="h-12 w-12 rounded-full bg-green-500/20 text-green-200 border border-green-500/30 flex items-center justify-center"
                            >
                                <FontAwesomeIcon
                                    icon={faCheck}
                                    className="h-6 w-6"
                                />
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <div className="mt-4 pt-3 border-t border-cyan-500/20">
                <div
                    className={`rounded-lg py-2 px-3 text-center font-bold text-xs tracking-wider ${profileStatusClass}`}
                >
                    {profileStatus}
                </div>
                <div className="mt-3 text-center">
                    <div className="mt-3 rounded-lg bg-cyan-500/10 border border-cyan-500/20 p-2 text-center">
                            <div className="text-[10px] uppercase text-cyan-300 tracking-wider">
                            🎉 Member Since
                        </div>
                        <div className="text-sm font-semibold text-white">
                            {new Date(authState?.user?.createdAt).toLocaleDateString(
                                "en-IN",
                                {
                                    day: "numeric",
                                    month: "long",
                                    year: "numeric",
                                }
                            )}
                        </div>
                        <div className="mt-2 text-[10px] text-gray-300 font-mono">
                            ⏳ {timeSinceJoin}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SnapId;